import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  DATE_FORMATS,
  guessMapping,
  parseCsv,
  toStatementLines,
  type BankCsvMapping,
  type BankImportResult,
  type BankProfile,
  type BankTransaction,
  type ImportBankStatement,
} from '@syndiqia/ledger/contracts';
import { errorMessage, useApi } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAccounts, useFunds, useLedgerMutation } from '../../ledger-queries';
import { useParties, useUnits } from '../../queries';
import { useAllOwnership } from '../../ledger-queries';
import { useSyndicate } from '../../session';
import { FinancesNav } from './FinancesNav';

type Status = BankTransaction['status'];

function useBankTransactions(status: Status) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'ledger', 'bank', status],
    queryFn: () => api<BankTransaction[]>(`${s.api}/ledger/bank-transactions?status=${status}`),
  });
}

export function BankPage() {
  const { t } = useI18n();
  const syndicate = useSyndicate();
  const [status, setStatus] = useState<Status>('unmatched');
  const transactions = useBankTransactions(status);
  const confirm = useLedgerMutation<string[], { confirmed: number }>((api, base, ids) =>
    api(`${base}/bank-transactions/confirm`, { method: 'POST', body: { transactionIds: ids } }),
  );

  if (!syndicate.can('ledger.read')) return <Alert tone="info">{t.common.forbidden}</Alert>;
  const reliable = transactions.data?.filter((tx) => tx.suggestion?.confidence === 'high') ?? [];

  return (
    <>
      <PageHeader
        title={t.finances.title}
        actions={syndicate.can('ledger.bank.reconcile') && <Disclosure label={t.bank.import}>{(close) => <ImportForm onDone={close} />}</Disclosure>}
      />
      <FinancesNav />
      <div className="toolbar">
        <nav className="subnav subnav-inline">
          {(['unmatched', 'matched', 'ignored'] as const).map((s) => (
            <button key={s} type="button" className={s === status ? 'active' : undefined} onClick={() => setStatus(s)}>
              {t.bank.tabs[s]}
            </button>
          ))}
        </nav>
        {status === 'unmatched' && reliable.length > 0 && syndicate.can('ledger.bank.reconcile') && (
          <button type="button" className="button button-primary" disabled={confirm.isPending} onClick={() => confirm.mutate(reliable.map((tx) => tx.id))}>
            {t.bank.confirmAll(reliable.length)}
          </button>
        )}
      </div>
      {confirm.isSuccess && <Alert tone="info">{t.bank.confirmed(confirm.data.confirmed)}</Alert>}
      {confirm.isError && <Alert>{errorMessage(confirm.error, t)}</Alert>}
      {transactions.isPending && <Loading label={t.loading} />}
      {transactions.data?.length === 0 && <Empty>{t.bank.none}</Empty>}
      {!!transactions.data?.length && (
        <ul className="bank-list">
          {transactions.data.map((tx) => (
            <TransactionRow key={tx.id} tx={tx} />
          ))}
        </ul>
      )}
    </>
  );
}

function TransactionRow({ tx }: { tx: BankTransaction }) {
  const { t, money, date } = useI18n();
  const { can } = useSyndicate();
  const units = useUnits();
  const ownership = useAllOwnership();
  const parties = useParties();
  const [open, setOpen] = useState(false);
  const confirm = useLedgerMutation<void, BankTransaction>((api, base) =>
    api(`${base}/bank-transactions/confirm`, { method: 'POST', body: { transactionIds: [tx.id] } }),
  );

  const unitLabel = (id: string) => units.data?.find((u) => u.id === id)?.label ?? '…';
  const ownerNames = (unitIds: string[]) =>
    [...new Set(ownership.data?.filter((o) => unitIds.includes(o.unitId)).map((o) => parties.data?.find((p) => p.id === o.partyId)?.displayName))]
      .filter(Boolean)
      .join(', ');
  const s = tx.suggestion;
  const suggestion = !s
    ? null
    : s.kind === 'entry'
      ? t.bank.entryLink(`${s.description} (${date(s.date)})`)
      : t.bank.paymentFor(`${s.units.map((u) => unitLabel(u.unitId)).join(', ')} — ${ownerNames(s.units.map((u) => u.unitId))}`);

  return (
    <li className={`bank-row bank-${tx.status}`}>
      <div className="bank-main">
        <span className="nowrap muted">{date(tx.date)}</span>
        <span className="bank-description">{tx.description}</span>
        <span className={`num ${tx.amountCents > 0 ? 'amount-in' : 'amount-out'}`}>{money(tx.amountCents)}</span>
      </div>
      {tx.status === 'unmatched' && can('ledger.bank.reconcile') && (
        <div className="bank-actions">
          {suggestion && s && (
            <span className="suggestion">
              <span className={`badge badge-${s.confidence === 'high' ? 'active' : 'draft'}`}>{t.bank.confidence[s.confidence]}</span> {suggestion}
            </span>
          )}
          {s && (
            <button type="button" className="button button-primary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
              {t.bank.confirm}
            </button>
          )}
          <button type="button" className="link-button" onClick={() => setOpen(!open)}>
            {t.bank.other}
          </button>
        </div>
      )}
      {tx.status === 'ignored' && tx.note && <div className="muted">{tx.note}</div>}
      {confirm.isError && <Alert>{errorMessage(confirm.error, t)}</Alert>}
      {open && <ManualMatch tx={tx} onDone={() => setOpen(false)} />}
    </li>
  );
}

function ManualMatch({ tx, onDone }: { tx: BankTransaction; onDone: () => void }) {
  const { t, locale } = useI18n();
  const units = useUnits();
  const accounts = useAccounts();
  const funds = useFunds();
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [accountNumber, setAccountNumber] = useState('');
  const base = `bank-transactions/${tx.id}`;
  const pay = useLedgerMutation<string[], BankTransaction>((api, b, ids) => api(`${b}/${base}/match-payment`, { method: 'POST', body: { unitIds: ids } }));
  const post = useLedgerMutation<string, BankTransaction>((api, b, number) => api(`${b}/${base}/post-entry`, { method: 'POST', body: { accountNumber: number } }));
  const ignore = useLedgerMutation<string, BankTransaction>((api, b, note) => api(`${b}/${base}/ignore`, { method: 'POST', body: { note } }));

  const fundKind = funds.data?.find((f) => f.code === tx.fundCode)?.kind;
  const bookable = accounts.data?.filter((a) => fundKind && a.allowedFundKinds.includes(fundKind) && !a.requiresUnit && a.role !== 'bank') ?? [];
  const error = pay.error ?? post.error ?? ignore.error;

  return (
    <div className="bank-manual">
      {tx.amountCents > 0 && (
        <div className="row-inline">
          <span className="field-label">{t.bank.payUnits}</span>
          <select multiple value={unitIds} onChange={(e) => setUnitIds([...e.target.selectedOptions].map((o) => o.value))} size={4}>
            {units.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
          <button type="button" className="button" disabled={unitIds.length === 0 || pay.isPending} onClick={() => pay.mutate(unitIds, { onSuccess: onDone })}>
            {t.bank.record}
          </button>
        </div>
      )}
      <div className="row-inline">
        <span className="field-label">{t.bank.postTo}</span>
        <select value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)}>
          <option value="">—</option>
          {bookable.map((a) => (
            <option key={a.number} value={a.number}>
              {a.number} — {locale === 'fr' ? a.nameFr : a.nameEn}
            </option>
          ))}
        </select>
        <button type="button" className="button" disabled={!accountNumber || post.isPending} onClick={() => post.mutate(accountNumber, { onSuccess: onDone })}>
          {t.bank.post}
        </button>
      </div>
      <button
        type="button"
        className="link-button"
        onClick={() => {
          const note = window.prompt(t.bank.ignoreReason);
          if (note?.trim()) ignore.mutate(note.trim(), { onSuccess: onDone });
        }}
      >
        {t.bank.ignore}
      </button>
      {error && <Alert>{errorMessage(error, t)}</Alert>}
    </div>
  );
}

function ImportForm({ onDone }: { onDone: () => void }) {
  const { t, money, date, locale } = useI18n();
  const api = useApi();
  const s = useSyndicate();
  const accounts = useAccounts();
  const funds = useFunds();
  const profiles = useQuery({ queryKey: [s.tenantId, 'ledger', 'bank-profiles'], queryFn: () => api<BankProfile[]>(`${s.api}/ledger/bank-profiles`) });

  const bankAccounts = (accounts.data ?? []).flatMap((a) =>
    a.role !== 'bank' ? [] : (funds.data ?? []).filter((f) => a.allowedFundKinds.includes(f.kind)).map((f) => ({ key: `${a.number}|${f.code}`, account: a, fund: f })),
  );
  const [target, setTarget] = useState('1010|OP');
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [mapping, setMapping] = useState<Partial<BankCsvMapping>>({});
  const rows = useMemo(() => (file ? parseCsv(file.text) : []), [file]);
  const complete = { skipRows: 1, dateFormat: 'YYYY-MM-DD' as const, decimalComma: false, descriptionColumns: [], ...mapping } as BankCsvMapping;
  const ready = complete.dateColumn !== undefined && complete.descriptionColumns.length > 0 && (complete.amountColumn !== undefined || (complete.withdrawalColumn !== undefined && complete.depositColumn !== undefined));
  const preview = ready ? toStatementLines(rows, complete) : null;

  const doImport = useLedgerMutation<ImportBankStatement, BankImportResult>((a, base, input) => a(`${base}/bank-imports`, { method: 'POST', body: input }));

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    const text = await f.text();
    setFile({ name: f.name, text });
    const [accountNumber, fundCode] = target.split('|');
    const saved = profiles.data?.find((p) => p.accountNumber === accountNumber && p.fundCode === fundCode);
    setMapping(saved?.mapping ?? { dateFormat: 'YYYY-MM-DD', decimalComma: /\d,\d{2}(?!\d)/.test(text), ...guessMapping(parseCsv(text)[0] ?? []) });
  };
  const header = rows[0] ?? [];
  const columnSelect = (key: 'dateColumn' | 'amountColumn' | 'withdrawalColumn' | 'depositColumn', label: string) => (
    <Field label={label}>
      <select value={complete[key] ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [key]: e.target.value === '' ? undefined : Number(e.target.value) }))}>
        <option value="">—</option>
        {header.map((h, i) => (
          <option key={i} value={i}>
            {i + 1}. {h}
          </option>
        ))}
      </select>
    </Field>
  );
  const twoColumns = complete.amountColumn === undefined;

  return (
    <div className="form">
      <div className="row">
        <Field label={t.bank.account}>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            {bankAccounts.map((b) => (
              <option key={b.key} value={b.key}>
                {b.account.number} — {locale === 'fr' ? b.fund.nameFr : b.fund.nameEn}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.bank.file}>
          <input type="file" accept=".csv,text/csv" onChange={(e) => void onFile(e.target.files?.[0])} />
        </Field>
      </div>

      {file && (
        <>
          <h3>{t.bank.mapping}</h3>
          <div className="row">
            <Field label={t.bank.skipRows}>
              <input type="number" min={0} max={20} value={complete.skipRows} onChange={(e) => setMapping((m) => ({ ...m, skipRows: Number(e.target.value) }))} />
            </Field>
            {columnSelect('dateColumn', t.bank.dateColumn)}
            <Field label={t.bank.dateFormat}>
              <select value={complete.dateFormat} onChange={(e) => setMapping((m) => ({ ...m, dateFormat: e.target.value as BankCsvMapping['dateFormat'] }))}>
                {DATE_FORMATS.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </Field>
            <Field label={t.bank.descriptionColumn}>
              <select
                value={complete.descriptionColumns[0] ?? ''}
                onChange={(e) => setMapping((m) => ({ ...m, descriptionColumns: e.target.value === '' ? [] : [Number(e.target.value)] }))}
              >
                <option value="">—</option>
                {header.map((h, i) => (
                  <option key={i} value={i}>
                    {i + 1}. {h}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="row">
            <Field label={t.bank.amountMode}>
              <select
                value={twoColumns ? 'two' : 'one'}
                onChange={(e) =>
                  setMapping((m) =>
                    e.target.value === 'one'
                      ? { ...m, amountColumn: m.withdrawalColumn ?? 0, withdrawalColumn: undefined, depositColumn: undefined }
                      : { ...m, amountColumn: undefined },
                  )
                }
              >
                <option value="one">{t.bank.oneColumn}</option>
                <option value="two">{t.bank.twoColumns}</option>
              </select>
            </Field>
            {twoColumns ? (
              <>
                {columnSelect('withdrawalColumn', t.bank.withdrawalColumn)}
                {columnSelect('depositColumn', t.bank.depositColumn)}
              </>
            ) : (
              columnSelect('amountColumn', t.bank.amountColumn)
            )}
            <label className="inline-field">
              <input type="checkbox" checked={complete.decimalComma} onChange={(e) => setMapping((m) => ({ ...m, decimalComma: e.target.checked }))} />
              {t.bank.decimalComma}
            </label>
          </div>

          {preview && (
            <>
              <h3>{t.bank.preview}</h3>
              {preview.errors.length > 0 && <Alert>{t.bank.unreadable(preview.errors.length)}</Alert>}
              <table className="table table-compact">
                <tbody>
                  {preview.lines.slice(0, 6).map((l) => (
                    <tr key={l.row}>
                      <td className="nowrap">{date(l.date)}</td>
                      <td>{l.description}</td>
                      <td className={`num ${l.amountCents > 0 ? 'amount-in' : 'amount-out'}`}>{money(l.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {doImport.isSuccess && <Alert tone="info">{t.bank.imported(doImport.data.imported, doImport.data.duplicates)}</Alert>}
          {doImport.isError && <Alert>{errorMessage(doImport.error, t)}</Alert>}
          <div className="form-actions">
            <button
              type="button"
              className="button button-primary"
              disabled={!preview || preview.errors.length > 0 || preview.lines.length === 0 || doImport.isPending || doImport.isSuccess}
              onClick={() => {
                const [accountNumber = '', fundCode = ''] = target.split('|');
                doImport.mutate({ accountNumber, fundCode, fileName: file.name, csv: file.text, mapping: complete });
              }}
            >
              {t.bank.doImport}
            </button>
            <button type="button" className="button" onClick={onDone}>
              {doImport.isSuccess ? t.common.back : t.common.cancel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
